import { useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { sphereColor, sphereName } from '../lifeos/spheres.js';
import Sheet from '../lifeos/Sheet.js';
import type { Sphere } from '../lifeos/types.js';
import Chip from './Chip.js';
import { IconButton } from './Button.js';
import Icon, { type IconName } from './Icon.js';
import { IconTile, MoreRow, Row } from './List.js';

/**
 * List-page kit (spec 2026-10-09 §6): the pieces every list page shares —
 * URL-backed filters, the sphere lens, the search field, a one-choice pick
 * sheet, and the small row parts (due word, sphere meta, avatar, fact row).
 */
/** One URL query param setter (replace, so filters never stack history). */
export function useParamSetter(): [URLSearchParams, (key: string, value: string | null) => void] {
  const [params, setParams] = useSearchParams();
  const set = (key: string, value: string | null): void =>
    setParams(
      (p) => {
        const n = new URLSearchParams(p);
        if (value) n.set(key, value);
        else n.delete(key);
        return n;
      },
      { replace: true },
    );
  return [params, set];
}

/**
 * The sphere lens (spec §1.4): a quiet funnel button in the page header, a
 * sheet to pick, and a removable chip under the title while one is set.
 */
export function useSphereLens(
  spheres: readonly Sphere[],
  value: string | null,
  onChange: (key: string | null) => void,
): { button: ReactNode; chip: ReactNode; sheet: ReactNode } {
  const [open, setOpen] = useState(false);
  return {
    button:
      spheres.length > 0 ? <IconButton icon="filter" label="Filter by sphere" active={value !== null} onClick={() => setOpen(true)} /> : null,
    chip: value ? (
      <Chip on dot={sphereColor(spheres as Sphere[], value)} onClick={() => onChange(null)} label={`Clear sphere ${sphereName(spheres as Sphere[], value)}`}>
        {sphereName(spheres as Sphere[], value)}
        <Icon name="x" size={14} />
      </Chip>
    ) : null,
    sheet: open ? (
      <PickSheet
        title="Show"
        label="Sphere"
        value={value}
        options={[{ key: null, name: 'All spheres' }, ...spheres.map((s) => ({ key: s.key, name: s.name, color: s.color ?? undefined }))]}
        onPick={(k) => {
          onChange(k);
          setOpen(false);
        }}
        onClose={() => setOpen(false)}
      />
    ) : null,
  };
}

/** A one-choice list in a sheet (sphere, kind…), iOS checkmark style. */
export function PickSheet({
  title,
  label,
  value,
  options,
  onPick,
  onClose,
}: {
  title: string;
  label: string;
  value: string | null;
  options: readonly { key: string | null; name: string; color?: string; dotClass?: string }[];
  onPick: (key: string | null) => void;
  onClose: () => void;
}): JSX.Element {
  return (
    <Sheet title={title} onClose={onClose}>
      <ul className="ui-group ui-group--flush" role="listbox" aria-label={label}>
        {options.map((o) => (
          <li key={o.key ?? 'all'} className="ui-swipe">
            <button type="button" role="option" aria-selected={value === o.key} className="ui-pick" onClick={() => onPick(o.key)}>
              {(o.color || o.dotClass || o.key === null) && (
                <span
                  className={`ui-dot${o.dotClass ? ` ${o.dotClass}` : ''}`}
                  style={o.dotClass ? undefined : { background: o.color ?? 'var(--text-muted)' }}
                  aria-hidden="true"
                />
              )}
              <span className="ui-pick__label">{o.name}</span>
              {value === o.key && <Icon name="check" size={20} className="ui-pick__check" />}
            </button>
          </li>
        ))}
      </ul>
    </Sheet>
  );
}

/** iOS search field: magnifier, rounded fill, clear button. */
export function SearchField({
  value,
  onChange,
  placeholder,
  label,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  label: string;
}): JSX.Element {
  return (
    <label className="la-search">
      <Icon name="search" size={18} className="la-search__icon" />
      <input type="search" placeholder={placeholder} aria-label={label} value={value} onChange={(e) => onChange(e.target.value)} />
      {value && (
        <button type="button" className="la-search__clear" aria-label="Clear search" onClick={() => onChange('')}>
          <Icon name="x" size={12} />
        </button>
      )}
    </label>
  );
}

/** A quiet due/expiry word; red only when late (spec §1.7). */
export function When({ tone, children }: { tone: string; children: ReactNode }): JSX.Element {
  return <span className={`la-when la-when--${tone}`}>{children}</span>;
}

/** Sphere dot + text, the start of a row's sub line. */
export function Meta({ color, children }: { color?: string; children?: ReactNode }): JSX.Element {
  return (
    <span className="la-meta">
      {color && <span className="ui-dot" style={{ background: color }} aria-hidden="true" />}
      {children}
    </span>
  );
}

/** Initials in a neutral circle; the sphere shows as a small dot on its edge. */
export function Avatar({ name, color, icon }: { name: string; color?: string; icon?: IconName }): JSX.Element {
  const initials = name
    .replace(/[^\p{L}\p{N} ]/gu, ' ')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
  return (
    <span className="la-avatar" aria-hidden="true">
      {icon ? <Icon name={icon} size={18} /> : initials || '?'}
      {color && <span className="la-avatar__dot" style={{ background: color }} />}
    </span>
  );
}

/** A label/value row for a detail page's facts group. */
export function Fact({ label, children, to }: { label: string; children: ReactNode; to?: string }): JSX.Element {
  return <Row className="la-fact" title={<span className="la-fact__label">{label}</span>} detail={<span className="la-fact__value">{children}</span>} to={to} />;
}

/**
 * The first `n` rows of a group, then "Show N more". Render it only when
 * `items` is non-empty (a Group counts it as a row).
 */
export function Fold<T>({
  items,
  n,
  render,
  noun = 'more',
}: {
  items: readonly T[];
  n: number;
  render: (item: T) => ReactNode;
  noun?: string;
}): JSX.Element {
  const [all, setAll] = useState(false);
  const shown = all ? items : items.slice(0, n);
  return (
    <>
      {shown.map(render)}
      <MoreRow count={items.length - shown.length} noun={noun} onClick={() => setAll(true)} />
    </>
  );
}

/** The icon tile an upcoming item / search hit gets, by type (colour = kind, not sphere). */
const TYPE_TILE: Record<string, [IconName, string]> = {
  obligation: ['receipt', '#ff9f0a'],
  birthday: ['star', '#ff375f'],
  document: ['file', '#8e8e93'],
  service: ['repeat', '#30b0c7'],
  warranty: ['shield', '#a2845e'],
  event: ['calendar', '#5e5ce6'],
  task: ['tasks', '#0a7c72'],
  person: ['users', '#0a84ff'],
  org: ['users', '#0a84ff'],
  project: ['folder', '#5e5ce6'],
  trip: ['plane', '#0a7c72'],
  life_note: ['bulb', '#ffb800'],
  message: ['chat', '#0a84ff'],
  belonging: ['package', '#a2845e'],
};
export function TypeTile({ type }: { type: string }): JSX.Element {
  const [icon, tint] = TYPE_TILE[type] ?? (['circle', '#8e8e93'] as [IconName, string]);
  return <IconTile name={icon} color={tint} />;
}
